import React from 'react';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { MarketEmptyState } from './MarketEmptyState';
import { formatPct, formatMultiple, formatMoney, formatPlacing } from '../../utils/marketFormatters';

const DOT_COLOR = '#1E6BFF';
const DOT_COLOR_WIN = '#C9A84C';
const GRID_COLOR = 'rgba(255,255,255,0.08)';
const TEXT_COLOR = '#94A3B8';

function ScatterTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="mk-chart-tooltip">
      <div className="mk-chart-tooltip-title">{p.runnerName} <span className="mk-chart-tooltip-sub">({p.slateName})</span></div>
      <div className="mk-chart-tooltip-row">Rating: {p.displayRating ?? '—'} · {p.valueLabel}</div>
      <div className="mk-chart-tooltip-row">Owned by: {formatPct(p.ownershipUserPct)}</div>
      <div className="mk-chart-tooltip-row">Return: {formatMultiple(p.returnMultiple)} ({formatMoney(p.virtualEarnings)})</div>
      <div className="mk-chart-tooltip-row">Finish: {formatPlacing(p.finishingPosition)}</div>
    </div>
  );
}

/**
 * Ownership % (X) vs return multiple (Y), bubble size = total purchase cost.
 * @param {Array} points - normalized scatter points from buildCrowdInsights
 */
export const MarketScatterChart = ({ points, height = 320 }) => {
  const data = (points || []).filter(p => p.ownershipUserPct != null && p.returnMultiple != null);
  if (data.length === 0) {
    return (
      <MarketEmptyState
        icon="🔎"
        message="More settled races are needed before ownership-vs-return trends become visible."
      />
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 8, right: 16, left: -8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
        <XAxis
          dataKey="ownershipUserPct" type="number" name="Owned By %"
          tickFormatter={v => formatPct(v)} domain={[0, 'dataMax']}
          tick={{ fill: TEXT_COLOR, fontSize: 12 }} axisLine={{ stroke: GRID_COLOR }} tickLine={false}
        />
        <YAxis
          dataKey="returnMultiple" type="number" name="Return Multiple"
          tickFormatter={v => formatMultiple(v)}
          tick={{ fill: TEXT_COLOR, fontSize: 12 }} axisLine={false} tickLine={false} width={56}
        />
        <ZAxis dataKey="totalPurchaseCost" range={[40, 320]} />
        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<ScatterTooltip />} />
        <Scatter
          data={data}
          fillOpacity={0.75}
          fill={DOT_COLOR}
          shape={(props) => {
            const { cx, cy, payload } = props;
            const isWinner = payload.finishingPosition === 1;
            const r = Math.max(4, Math.min(16, Math.sqrt((payload.totalPurchaseCost || 1)) / 4));
            return <circle cx={cx} cy={cy} r={r} fill={isWinner ? DOT_COLOR_WIN : DOT_COLOR} fillOpacity={0.75} />;
          }}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
};

export default MarketScatterChart;
